// src/pages/Calendar.jsx
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import {
  createCalendar,
  deleteCalendar,
  getCalendars,
  updateCalendar,
} from "../services/calendarService";
import { getDrivers } from "../services/driverService";
import parseStringDate from "../utils/parseStringDate";

// Import Calendar Components
import CalendarFilters from "../components/calendar/CalendarFilters";
import CalendarControls from "../components/calendar/CalendarControls";
import CalendarGrid from "../components/calendar/CalendarGrid";
import EventListView from "../components/calendar/EventListView";
import EventModal from "../components/calendar/EventModal";

/**
 * Main Calendar Component
 *
 * Manages the overall calendar state and coordinates between different views.
 * Handles data loading, event CRUD operations, and view switching.
 */
const Calendar = () => {
  // ==========================================
  // STATE MANAGEMENT
  // ==========================================

  // URL search parameters for event type filtering
  const [searchParams, setSearchParams] = useSearchParams();
  const eventTypeFilter = searchParams.get("eventType");

  // View mode: 'calendar' or 'list'
  const [viewMode, setViewMode] = useState("calendar");

  // Event filtering state
  const [activeFilter, setActiveFilter] = useState(eventTypeFilter || "all");

  // Search state
  const [searchQuery, setSearchQuery] = useState("");

  // Calendar events array
  const [events, setEvents] = useState([]);

  // Drivers lookup map — { [uid]: driverDoc } — used to render driver
  // name badges on event cards. Loaded once on mount; refreshes when an
  // event changes its driverId via handleSaveEvent.
  const [driversById, setDriversById] = useState({});

  // Modal state
  const [isAddEventModal, setIsAddEventModal] = useState(false);
  const [eventType, setEventType] = useState("");

  // Current date for calendar navigation
  const [currentDate, setCurrentDate] = useState(new Date());

  // Form parameters
  const defaultParams = {
    id: null,
    title: "",
    location: "",
    start: "",
    end: "",
    description: "",
    jobId: null,
    customerId: null,
    driverId: "",
  };
  const [params, setParams] = useState(defaultParams);

  // ==========================================
  // DATA LOADING
  // ==========================================

  /**
   * Update active filter when URL parameter changes
   */
  useEffect(() => {
    const newFilter = eventTypeFilter || "all";
    if (newFilter !== activeFilter) {
      setActiveFilter(newFilter);
    }
  }, [eventTypeFilter, activeFilter]);

  /**
   * Load calendar events on component mount
   */
  useEffect(() => {
    const loadCalendarEvents = async () => {
      try {
        const calendarEvents = await getCalendars();
        if (calendarEvents && calendarEvents.length > 0) {
          setEvents(calendarEvents);
        }
      } catch (error) {
        console.error("Error loading calendar events:", error);
        showMessage("Failed to load calendar events", "error");
      }
    };

    loadCalendarEvents();
  }, []);

  /**
   * Load drivers once on mount so we can render their names on event cards
   * without an extra fetch per card. Include disabled drivers — events
   * assigned to a now-disabled driver should still display the name.
   */
  useEffect(() => {
    let cancelled = false;
    getDrivers({ includeDisabled: true })
      .then((list) => {
        if (cancelled) return;
        const map = {};
        list.forEach((d) => {
          map[d.id] = d;
        });
        setDriversById(map);
      })
      .catch((err) => {
        // Non-fatal — the dropdown in EventModal handles its own load.
        console.warn("Failed to load drivers for badges:", err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ==========================================
  // EVENT FILTERING
  // ==========================================

  /**
   * Filter events based on selected filter type and search query
   */
  const getFilteredEvents = () => {
    let filteredEvents = events;

    // Filter by event type
    if (activeFilter !== "all") {
      filteredEvents = filteredEvents.filter(
        (event) => event.eventType === activeFilter
      );
    }

    // Filter by customer name (search in title)
    if (searchQuery.trim()) {
      filteredEvents = filteredEvents.filter((event) =>
        event.title?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filteredEvents;
  };

  /**
   * Handle filter changes
   */
  const handleFilterChange = (filterType) => {
    setActiveFilter(filterType);
  };

  // ==========================================
  // EVENT CRUD OPERATIONS
  // ==========================================

  /**
   * Open modal for creating/editing events
   */
  const handleEditEvent = (event = null, selectedDate = null) => {
    if (event) {
      // Edit existing event
      setParams({
        id: event.id,
        title: event.title || "",
        location: event.location || "",
        start: event.start
          ? new Date(parseStringDate(event.start)).toISOString().slice(0, 16)
          : "",
        end: event.end
          ? new Date(parseStringDate(event.end)).toISOString().slice(0, 16)
          : "",
        description: event.description || "",
        jobId: event.jobId || null,
        customerId: event.customerId || null,
        driverId: event.driverId || "",
      });
      setEventType(event.eventType || "");
    } else if (selectedDate) {
      // Create new event for selected date
      setParams({
        ...defaultParams,
        start: selectedDate.toISOString().slice(0, 16),
        end: new Date(selectedDate.getTime() + 3600000)
          .toISOString()
          .slice(0, 16), // +1 hour
      });
      setEventType("");
    } else {
      // Create new event
      setParams(defaultParams);
      setEventType("");
    }

    setIsAddEventModal(true);
  };

  /**
   * Format date for backend API
   * Try different formats to match backend expectations
   */
  const formatDateForAPI = (dateTimeString) => {
    if (!dateTimeString) return "";

    const date = new Date(dateTimeString);

    // Try different formats based on common backend expectations

    // Format 1: ISO String (most common for APIs)
    const isoFormat = date.toISOString();

    // Let's try the ISO format first as it's most common
    return isoFormat;
  };

  /**
   * Save event (create or update)
   */
  const handleSaveEvent = async () => {
    // Validation
    const emptyFields = [];

    if (!params.title.trim()) emptyFields.push("Title");
    if (!params.start) emptyFields.push("Start Date");
    if (!params.end) emptyFields.push("End Date");
    if (!params.id && !eventType) emptyFields.push("Event Type");

    if (emptyFields.length > 0) {
      showMessage(
        `Please fill in the required fields: ${emptyFields.join(", ")}`,
        "error"
      );
      return;
    }

    try {
      if (params.id) {
        // Update existing event
        const updatedEvent = {
          id: params.id,
          title: params.title,
          location: params.location || "",
          start: formatDateForAPI(params.start),
          end: formatDateForAPI(params.end),
          description: params.description,
          eventType:
            eventType || events.find((e) => e.id === params.id)?.eventType,
          jobId: params.jobId,
          customerId: params.customerId,
          driverId: params.driverId || null,
        };

        await updateCalendar(params.id, updatedEvent);
        setEvents((prev) =>
          prev.map((e) => (e.id === params.id ? updatedEvent : e))
        );
        showMessage("Event updated successfully", "success");
      } else {
        // Create new event
        const newEvent = {
          title: params.title,
          location: params.location || "",
          start: formatDateForAPI(params.start),
          end: formatDateForAPI(params.end),
          description: params.description,
          eventType: eventType,
          // Default sortOrder so newly created events sit at the end of their
          // day's list. Manual reorders later overwrite this with 0,1,2,...
          sortOrder: Date.now(),
          driverId: params.driverId || null,
        };

        const createdEvent = await createCalendar(newEvent);

        if (createdEvent) {
          setEvents((prev) => [...prev, { ...newEvent, id: createdEvent.id }]);
          showMessage("Event created successfully", "success");
        }
      }

      // Close modal and reset
      setIsAddEventModal(false);
      setParams(defaultParams);
      setEventType("");
    } catch (error) {
      console.error("Error saving event:", error);
      showMessage("Failed to save event. Please try again.", "error");
    }
  };

  /**
   * Reorder events within a single day.
   *
   * Receives the day's events in their new visual order, assigns each one a
   * fresh contiguous sortOrder (0,1,2,...), updates local state immediately
   * for snappy UI, then persists each new sortOrder to Firestore. Errors are
   * surfaced but local state is intentionally not rolled back — a hard refresh
   * will reconcile against the server.
   */
  const handleReorderDay = async (reorderedDayEvents) => {
    const updates = reorderedDayEvents.map((event, idx) => ({
      ...event,
      sortOrder: idx,
    }));

    // Apply optimistically.
    setEvents((prev) => {
      const byId = new Map(updates.map((e) => [e.id, e]));
      return prev.map((e) => (byId.has(e.id) ? byId.get(e.id) : e));
    });

    // Persist. Use sequential awaits to keep error surface obvious; the
    // payload per day is small so parallel save isn't a meaningful win.
    try {
      for (const evt of updates) {
        await updateCalendar(evt.id, { sortOrder: evt.sortOrder });
      }
    } catch (error) {
      console.error("Error saving reordered events:", error);
      showMessage("Failed to save new order. Try again.", "error");
    }
  };

  /**
   * Delete event
   */
  const handleDeleteEvent = async (eventId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This event will be marked as completed and removed from the calendar.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#10b981",
      cancelButtonColor: "#ef4444",
      confirmButtonText: "Yes, mark as done!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await deleteCalendar(eventId);
        setEvents((prev) => prev.filter((e) => e.id !== eventId));
        setIsAddEventModal(false);
        showMessage("Event marked as completed successfully", "success");
      } catch (error) {
        console.error("Error deleting event:", error);
        showMessage("Failed to delete event. Please try again.", "error");
      }
    }
  };

  // ==========================================
  // UTILITY FUNCTIONS
  // ==========================================

  /**
   * Show toast notification
   */
  const showMessage = (msg = "", type = "success") => {
    Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      customClass: { container: "toast" },
    }).fire({
      icon: type,
      title: msg,
      padding: "10px 20px",
    });
  };

  /**
   * Handle form input changes
   */
  const handleInputChange = (e) => {
    const { value, id } = e.target;
    setParams((prev) => ({ ...prev, [id]: value }));
  };

  /**
   * Handle event type changes
   */
  const handleEventTypeChange = (type) => {
    setEventType((prevType) => (prevType === type ? "" : type));
  };

  // ==========================================
  // RENDER
  // ==========================================

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Calendar</h1>
        <p className="text-gray-600">Manage your appointments and events</p>
      </div>

      {/* Filters */}
      <CalendarFilters
        events={events}
        activeFilter={activeFilter}
        onFilterChange={handleFilterChange}
      />

      {/* Search Box */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <input
            type="text"
            placeholder="Search by customer name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 shadow-sm bg-white"
          />
          {/* Search Icon */}
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          {/* Clear Search Button */}
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
              title="Clear search"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
        {/* Results Count */}
        {searchQuery && (
          <p className="mt-2 text-sm text-gray-600">
            Found {getFilteredEvents().length} event
            {getFilteredEvents().length !== 1 ? "s" : ""} matching "
            {searchQuery}"
          </p>
        )}
      </div>

      {/* Controls */}
      <CalendarControls
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        onCreateEvent={() => handleEditEvent()}
      />

      {/* Content Area */}
      {viewMode === "calendar" ? (
        <CalendarGrid
          events={getFilteredEvents()}
          currentDate={currentDate}
          onDateChange={setCurrentDate}
          onEditEvent={handleEditEvent}
          onReorderDay={handleReorderDay}
          driversById={driversById}
        />
      ) : (
        <EventListView
          events={getFilteredEvents()}
          onEditEvent={handleEditEvent}
          searchQuery={searchQuery}
          onReorderDay={handleReorderDay}
          driversById={driversById}
        />
      )}

      {/* Event Modal */}
      <EventModal
        isOpen={isAddEventModal}
        params={params}
        eventType={eventType}
        onClose={() => {
          setIsAddEventModal(false);
          setParams(defaultParams);
          setEventType("");
        }}
        onInputChange={handleInputChange}
        onEventTypeChange={handleEventTypeChange}
        onSave={handleSaveEvent}
        onDelete={handleDeleteEvent}
      />
    </div>
  );
};

export default Calendar;
